import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Pencil, Check, X as XIcon, Archive, MapPin, Building2,
  FileText, ListChecks, ClipboardList, Wallet, Calendar, Users2, Mail,
  PauseCircle, PlayCircle, ExternalLink, Eye,
} from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { AdminLocationSelector, COUNTRY_NAMES_ES } from "./AdminLocationSelector";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOB_TYPES = [
  { value: "full-time", label: "Tiempo completo" },
  { value: "part-time", label: "Medio tiempo" },
  { value: "contract", label: "Contrato" },
  { value: "temporary", label: "Temporal" },
  { value: "internship", label: "Prácticas / Pasantía" },
  { value: "freelance", label: "Por proyecto / Freelance" },
  { value: "hourly", label: "Por horas" },
  { value: "seasonal", label: "Estacional" },
];
const CURRENCIES = [
  { code: "EUR", symbol: "€" }, { code: "USD", symbol: "US$" },
  { code: "UYU", symbol: "$U" }, { code: "ARS", symbol: "AR$" }, { code: "BRL", symbol: "R$" },
];
const ROLE_LABELS = { owner: "Propietario", admin: "Administrador", recruiter: "Reclutador" };
const STAGE_LABELS = { new_applicant: "Postulado", contacted: "Contactado", interview: "Entrevista", offer: "Oferta", hired: "Contratado", rejected: "Rechazado" };

function InfoRow({ icon: Icon, label, value, editing, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        {editing ? children : <p className="text-sm text-slate-800 break-words whitespace-pre-wrap">{value || "—"}</p>}
      </div>
    </div>
  );
}

const editFormFrom = (j) => ({
  title: j.title || "", description: j.description || "",
  requirements: j.requirements || [], responsibilities: j.responsibilities || [],
  job_type: j.job_type || "full-time", remote: !!j.remote,
  salary_min: j.salary_min ?? "", salary_max: j.salary_max ?? "", salary_currency: j.salary_currency || "USD",
  country_code: j.country_code || null, admin1_code: j.admin1_code || null, admin1_name: j.admin1_name || null,
  admin2_code: j.admin2_code || null, admin2_name: j.admin2_name || null, city: j.city || null,
  latitude: j.latitude || null, longitude: j.longitude || null, location_verified: !!j.location_verified,
});

export default function AdminJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [applicationsOpen, setApplicationsOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const hasLoadedRef = React.useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs/${jobId}`, { headers: getAdminAuthHeaders() });
      setDetail(res.data);
      setForm(editFormFrom(res.data));
      if (res.data.company_id) {
        const cRes = await axios.get(`${API}/admin/companies/${res.data.company_id}`, { headers: getAdminAuthHeaders() }).catch(() => null);
        setCompany(cRes?.data || null);
      }
      hasLoadedRef.current = true;
    } catch (err) {
      toast.error("No se pudo cargar la ficha del empleo");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (searchParams.get("archive") === "1" && detail) {
      setConfirmArchive(true);
      searchParams.delete("archive");
      setSearchParams(searchParams, { replace: true });
    }
  }, [detail, searchParams, setSearchParams]);

  const errMsg = (err, fallback) => {
    const d = err?.response?.data?.detail;
    return d ? translateAdminError(d) : fallback;
  };

  const startEdit = () => { setActiveTab("info"); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setForm(editFormFrom(detail)); };
  const saveEdit = async () => {
    try {
      const payload = { ...form, salary_min: form.salary_min === "" ? null : Number(form.salary_min), salary_max: form.salary_max === "" ? null : Number(form.salary_max) };
      await axios.put(`${API}/admin/jobs/${jobId}`, payload, { headers: getAdminAuthHeaders() });
      toast.success("Empleo actualizado");
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar el empleo"));
    }
  };

  const toggleStatus = async () => {
    setStatusLoading(true);
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/pause`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.title} pausado`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar el estado"));
    } finally {
      setStatusLoading(false);
    }
  };

  // Reactivación (desde "paused" o "archived") — bloqueada por el backend si la
  // empresa dueña del empleo está archivada/suspendida (mensaje claro al admin).
  const reactivateJob = async () => {
    setStatusLoading(true);
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/reactivate`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.title} reactivado`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo reactivar el empleo"));
    } finally {
      setStatusLoading(false);
    }
  };

  const confirmArchiveAction = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/archive`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.title} fue archivado`);
      setConfirmArchive(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo archivar el empleo"));
    } finally {
      setArchiving(false);
    }
  };

  // Reabrir: SOLO para empleos cerrados automáticamente al archivar su empresa
  // (closed_reason === "company_archived"). Reutiliza el mismo registro — nunca
  // duplica ni crea un empleo nuevo. Bloqueado por el backend si la empresa
  // todavía no está activa.
  const confirmReopenAction = async () => {
    setReopening(true);
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/reopen`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.title} fue reabierto`);
      setConfirmReopen(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo reabrir el empleo"));
    } finally {
      setReopening(false);
    }
  };

  const openApplications = async () => {
    setApplicationsOpen(true);
    setApplicationsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs/${jobId}/applications`, { headers: getAdminAuthHeaders() });
      setApplications(res.data.applications || []);
    } catch (err) {
      toast.error(errMsg(err, "No se pudieron cargar las aplicaciones"));
    } finally {
      setApplicationsLoading(false);
    }
  };

  if (loading || !detail) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-job-detail-loading">
        <Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  const owner = company?.members?.find((m) => m.role === "owner");
  const recruiters = company?.members?.filter((m) => m.role !== "owner") || [];

  return (
    <div data-testid="admin-job-detail-page" className="max-w-4xl">
      <button data-testid="admin-job-back-button" onClick={() => navigate("/admin/jobs")} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver a Empleos
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 data-testid="admin-job-detail-title" className="font-poppins text-xl font-bold text-slate-900 truncate">{detail.title}</h1>
            <button data-testid="admin-job-detail-company-link" onClick={() => navigate(`/admin/companies/${detail.company_id}`)} className="text-sm text-[#028CF8] hover:underline flex items-center gap-1 mt-0.5">
              <Building2 className="h-3.5 w-3.5" /> {detail.company_name}
            </button>
            <div className="flex items-center gap-2 mt-2">
              <StatusBadge value={detail.status} testId="admin-job-detail-status" />
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">{detail.remote ? "Remoto" : "Presencial"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="admin-job-header-edit" variant="outline" size="sm" onClick={startEdit}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
            </Button>
            {detail.status === "active" && (
              <Button data-testid="admin-job-header-toggle-status" variant="outline" size="sm" disabled={statusLoading} onClick={toggleStatus}
                className="text-amber-600 border-amber-200">
                <PauseCircle className="h-3.5 w-3.5 mr-1.5" /> Pausar
              </Button>
            )}
            {(detail.status === "paused" || detail.status === "archived") && (
              <Button data-testid="admin-job-header-reactivate" variant="outline" size="sm" disabled={statusLoading} onClick={reactivateJob}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Reactivar empleo
              </Button>
            )}
            {detail.status === "closed" && detail.closed_reason === "company_archived" && (
              <Button data-testid="admin-job-header-reopen" variant="outline" size="sm" onClick={() => setConfirmReopen(true)}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Reabrir empleo
              </Button>
            )}
            {detail.status !== "archived" && (
              <Button data-testid="admin-job-header-archive" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmArchive(true)}>
                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivar / Eliminar
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="admin-job-tabs" className="mb-4 bg-slate-100">
          <TabsTrigger data-testid="admin-job-tab-info" value="info">Información general</TabsTrigger>
          <TabsTrigger data-testid="admin-job-tab-ops" value="ops">Datos operativos</TabsTrigger>
        </TabsList>

        {/* ── Información general ── */}
        <TabsContent value="info" data-testid="admin-job-tab-content-info">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Detalle del puesto</h3>
              {!editing ? (
                <button data-testid="admin-job-edit-toggle" onClick={startEdit} className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button data-testid="admin-job-edit-cancel" onClick={cancelEdit} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:underline">
                    <XIcon className="h-3.5 w-3.5" /> Cancelar
                  </button>
                  <button data-testid="admin-job-edit-save" onClick={saveEdit} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline">
                    <Check className="h-3.5 w-3.5" /> Guardar
                  </button>
                </div>
              )}
            </div>

            <InfoRow icon={FileText} label="Título" value={detail.title} editing={editing}>
              <Input data-testid="admin-job-edit-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={Building2} label="Empresa" value={detail.company_name} editing={false} />
            <InfoRow icon={FileText} label="Descripción" value={detail.description} editing={editing}>
              <Textarea data-testid="admin-job-edit-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="text-sm min-h-[90px]" />
            </InfoRow>
            <InfoRow icon={ListChecks} label="Requisitos" value={(detail.requirements || []).join(" · ") || null} editing={editing}>
              <RequirementsEditor items={form.requirements} onChange={(v) => setForm((f) => ({ ...f, requirements: v }))} />
            </InfoRow>
            <InfoRow icon={ClipboardList} label="Responsabilidades" value={(detail.responsibilities || []).join(" · ") || null} editing={editing}>
              <RequirementsEditor items={form.responsibilities} onChange={(v) => setForm((f) => ({ ...f, responsibilities: v }))} />
            </InfoRow>

            {!editing ? (
              <InfoRow icon={MapPin} label="Ubicación" value={[detail.city, detail.admin1_name, COUNTRY_NAMES_ES[detail.country_code]].filter(Boolean).join(", ") || detail.location} editing={false} />
            ) : (
              <div className="py-3 border-b border-slate-100">
                <p className="text-xs text-slate-400 mb-2 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Ubicación</p>
                <AdminLocationSelector
                  countryCode={form.country_code} admin1Code={form.admin1_code} admin1Name={form.admin1_name}
                  admin2Code={form.admin2_code} admin2Name={form.admin2_name} city={form.city}
                  locationVerified={form.location_verified} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                />
              </div>
            )}

            <InfoRow icon={ClipboardList} label="Modalidad" value={detail.remote ? "Remoto" : "Presencial"} editing={editing}>
              <div className="flex gap-2">
                {[{ v: false, l: "Presencial" }, { v: true, l: "Remoto" }].map((opt) => (
                  <button key={opt.l} type="button" data-testid={`admin-job-edit-modality-${opt.v}`} onClick={() => setForm((f) => ({ ...f, remote: opt.v }))}
                    className={`h-8 px-3 rounded-lg text-xs font-semibold border ${form.remote === opt.v ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </InfoRow>
            <InfoRow icon={ClipboardList} label="Tipo de empleo" value={JOB_TYPES.find((t) => t.value === detail.job_type)?.label || detail.job_type} editing={editing}>
              <select data-testid="admin-job-edit-type" value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))} className="w-full h-8 px-2 rounded-md border border-slate-200 text-sm bg-white">
                {JOB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </InfoRow>
            <InfoRow icon={Wallet} label="Salario" value={detail.salary_min || detail.salary_max ? `${detail.salary_min || "—"} - ${detail.salary_max || "—"} ${detail.salary_currency || ""}` : "A convenir"} editing={editing}>
              <div className="grid grid-cols-3 gap-2">
                <Input data-testid="admin-job-edit-salary-min" type="number" value={form.salary_min} onChange={(e) => setForm((f) => ({ ...f, salary_min: e.target.value }))} className="h-8 text-sm" placeholder="Mín." />
                <Input data-testid="admin-job-edit-salary-max" type="number" value={form.salary_max} onChange={(e) => setForm((f) => ({ ...f, salary_max: e.target.value }))} className="h-8 text-sm" placeholder="Máx." />
                <select data-testid="admin-job-edit-currency" value={form.salary_currency} onChange={(e) => setForm((f) => ({ ...f, salary_currency: e.target.value }))} className="h-8 px-2 rounded-md border border-slate-200 text-sm bg-white">
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
            </InfoRow>
            <InfoRow icon={Calendar} label="Fecha de publicación" value={detail.posted_date ? new Date(detail.posted_date).toLocaleDateString() : "—"} editing={false} />
            <InfoRow icon={ClipboardList} label="Estado" value={<StatusBadge value={detail.status} />} editing={false} />
          </div>
        </TabsContent>

        {/* ── Datos operativos ── */}
        <TabsContent value="ops" data-testid="admin-job-tab-content-ops">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-3">Empresa asociada</h3>
              <button data-testid="admin-job-ops-company-link" onClick={() => navigate(`/admin/companies/${detail.company_id}`)} className="flex items-center gap-2 text-sm font-medium text-[#028CF8] hover:underline">
                <Building2 className="h-4 w-4" /> {detail.company_name} <ExternalLink className="h-3 w-3" />
              </button>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-3">Propietario / Reclutadores</h3>
              {owner ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100">
                    <Users2 className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{owner.name} <span className="text-xs text-purple-600 font-semibold ml-1">Propietario</span></p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {owner.email}</p>
                    </div>
                  </div>
                  {recruiters.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100">
                      <Users2 className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{m.name} <span className="text-xs text-blue-600 font-semibold ml-1">{ROLE_LABELS[m.role]}</span></p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {m.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500">Sin datos de la empresa</p>}
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-3">Aplicaciones</h3>
              <div className="flex items-center gap-3">
                <span data-testid="admin-job-applications-count" className="text-2xl font-bold text-slate-900">{detail.applications_count || 0}</span>
                <span className="text-sm text-slate-500">aplicaciones recibidas</span>
                {(detail.applications_count || 0) > 0 && (
                  <button data-testid="admin-job-view-applications" onClick={openApplications} className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline ml-2">
                    <Eye className="h-3.5 w-3.5" /> Ver aplicaciones
                  </button>
                )}
              </div>
              {applicationsOpen && (
                <div data-testid="admin-job-applications-panel" className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
                  {applicationsLoading ? (
                    <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 text-[#028CF8] animate-spin" /></div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                        <tr><th className="text-left px-3 py-2">Candidato</th><th className="text-left px-3 py-2">Etapa</th><th className="text-left px-3 py-2">Aplicó</th></tr>
                      </thead>
                      <tbody>
                        {applications.map((a, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <p className="font-medium text-slate-800">{a.name}</p>
                              <p className="text-xs text-slate-500">{a.email}</p>
                            </td>
                            <td className="px-3 py-2 text-slate-600">{STAGE_LABELS[a.stage] || a.stage}</td>
                            <td className="px-3 py-2 text-slate-500">{a.applied_date ? new Date(a.applied_date).toLocaleDateString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="text-xs text-slate-600 p-2 bg-slate-50">Solo lectura — nombre, email, etapa y fecha. No incluye CV ni otros datos sensibles.</p>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-400 mb-1">Fecha de creación</p>
                <p className="text-sm text-slate-700">{detail.posted_date ? new Date(detail.posted_date).toLocaleDateString() : "—"}</p>
              </div>
              <div className="p-3 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-400 mb-1">Última actualización</p>
                <p className="text-sm text-slate-700" data-testid="admin-job-updated-at">{detail.updated_at ? new Date(detail.updated_at).toLocaleDateString() : "—"}</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Confirmación de archivado ── */}
      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="admin-job-archive-confirm" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar "{detail.title}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1.5 text-sm text-slate-700">
                  <p className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-400" /> Empresa: {detail.company_name}</p>
                  <p className="flex items-center gap-2"><Users2 className="h-3.5 w-3.5 text-slate-400" /> {detail.applications_count || 0} aplicación(es) recibidas</p>
                  <p className="flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5 text-slate-400" /> Estado actual: <StatusBadge value={detail.status} /></p>
                  <p className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-slate-400" /> Publicado: {detail.posted_date ? new Date(detail.posted_date).toLocaleDateString() : "—"}</p>
                </div>
                <p className="text-slate-600">Al confirmar: el empleo se oculta de la app pública y queda archivado. Las aplicaciones, el historial y la relación con la empresa se conservan. No se borra nada físicamente.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-job-archive-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-job-archive-confirm-button" onClick={confirmArchiveAction} disabled={archiving} className="bg-red-600 hover:bg-red-700">
              {archiving ? "Archivando..." : "Archivar empleo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* ── Confirmación de reapertura (empleo cerrado al archivar la empresa) ── */}
      <AlertDialog open={confirmReopen} onOpenChange={setConfirmReopen}>
        <AlertDialogContent data-testid="admin-job-reopen-confirm" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reabrir "{detail.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Este empleo se cerró automáticamente cuando se archivó su empresa. Al
              confirmar, vuelve a publicarse como activo con el mismo registro —
              conserva título, descripción, requisitos, salario, ubicación,
              aplicaciones e historial. No se crea un empleo nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-job-reopen-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-job-reopen-confirm-button" onClick={confirmReopenAction} disabled={reopening} className="bg-emerald-600 hover:bg-emerald-700">
              {reopening ? "Reabriendo..." : "Reabrir empleo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RequirementsEditor({ items, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (!draft.trim()) return; onChange([...items, draft.trim()]); setDraft(""); };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8 text-sm"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button type="button" variant="outline" size="sm" onClick={add}>+</Button>
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1">
            <span className="truncate">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 shrink-0 text-xs">✕</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
